import React, { useState, useEffect, memo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
// import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

import DialogFormSchedule from './DialogFormSchedule';
import { useStore } from 'src/context/StoreContext';
import {
  stickyHeader
} from 'src/components/calendar/styles';
import { getToken } from 'src/utils/auth';
import { getAPI } from 'src/utils/api';
import dayjs from 'dayjs';
import { getField } from 'src/common/helper';

const days = [
  '日',
  '月',
  '火',
  '水',
  '木',
  '金',
  '土'
];

const colorBorderTableHead = '#DDDDDD';
const colorBorderTableBody = '#F3F3F3';
const heightLine = 16;

const dataShowDialogFormDefine = {
  open: false,
  data: {
    startDate: '',
    endDate: '',
    doctorid: null
  }
};

type SchedulesType = {
  can: number;
  doctors: any;
  schedules: any[];
};

type dataShowDialogFormType = {
  open: boolean;
  data: {
    startDate: string;
    endDate: string;
    doctorid: number | null;
  }
};

type WorkSchduleProps = {
  openFilter: boolean;
  dateSelected: string;
  yearMonth: string;
};

const WorkSchdule: React.FC<WorkSchduleProps> = (props) => {
  const storeContext = useStore();
  const store = storeContext.store;
  const setOptionFilter = storeContext.setOptionFilter;
  const optionFilterSelected = storeContext.optionFilterSelected;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<SchedulesType>();

  const [dataShowDialogForm, setDataShowDialogForm] = useState<dataShowDialogFormType>(dataShowDialogFormDefine);

  const handleOpenDialogEdit = (weekData: any[], indexDay: number, doctorid: number) => () => {
    let startDate = '';
    let endDate = '';
    let check = false;

    for (let index = 0; index < weekData.length; index++) {
      const day = weekData[index];
      const doctors = getField(day, 'doctors', {});

      if (doctors[doctorid]) {
        startDate = startDate === '' ? getField(day, 'day', '') : startDate;
        check = indexDay === index || check ? true : false;

        if (!checkHasDoctorNext(weekData, index, doctorid)) {
          if (check) {
            endDate = getField(day, 'day', '');
            break;
          } else {
            startDate = '';
          }
        }
      }
    }

    setDataShowDialogForm({
      open: true,
      data: {
        startDate: startDate,
        endDate: endDate,
        doctorid
      }
    })
  };

  const handleOpenDialog = (date: string) => () => {
    setDataShowDialogForm({
      open: true,
      data: {
        startDate: date,
        endDate: date,
        doctorid: null
      }
    })
  };

  const handleCloseDialog = (isRefreshList?: boolean) => {
    if (isRefreshList) {
      getCalendarMonth(props.yearMonth);
    }
    
    setDataShowDialogForm(dataShowDialogFormDefine);
  };

  const formatDoctorsData = (data: any[]) => {
    const doctors = data;

    const doctorsObject: any = doctors
      ? doctors.reduce((acc: any, doctor: any) => {
        acc[doctor.id] = doctor;
        return acc;
      }, {} as any)
      : {};

    return doctorsObject;
  };

  const formatSchedules = (schedules: any[]) => {
    let temp = schedules;

    for (let index = 0; index < temp.length; index++) {
      for (let indexDay = 0; indexDay < temp[index].length; indexDay++) {
        temp[index][indexDay].doctors = formatDoctorsData(temp[index][indexDay].doctors);
      }
    }

    return temp;
  };

  const checkHasDoctorPrev = (weekData: any[], indexDay: number, doctorid: number) => {
    const doctors = weekData[indexDay - 1] ? weekData[indexDay - 1].doctors : {};

    return doctors[doctorid] ? true : false;
  };

  const checkHasDoctorNext = (weekData: any[], indexDay: number, doctorid: number) => {
    const doctors = weekData[indexDay + 1] ? weekData[indexDay + 1].doctors : {};

    return doctors[doctorid] ? true : false;
  };

  const checkDiffMonth = (weekData: any[], indexDay: number) => {
    const nextDate = weekData[indexDay + 1] ? weekData[indexDay + 1].day : '';
    const prevDate = weekData[indexDay - 1] ? weekData[indexDay - 1].day : '';
    const date = weekData[indexDay] ? weekData[indexDay].day : '';

    return ((nextDate !== '' && dayjs(nextDate).format('YYYY-MM') === props.yearMonth)
      || (prevDate !== '' && dayjs(prevDate).format('YYYY-MM') === props.yearMonth))
      && dayjs(date).format('YYYY-MM') !== props.yearMonth;
  };


  const checkDateDiffMonth = (date: string) => {
    return dayjs(date).format('YYYY-MM') !== props.yearMonth
  };

  const getCalendarMonth = async (yearMonth: string) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      setIsLoading(true);

      const res = await getAPI(
        `/calendar/month?month=${yearMonth}&token=${token}`
      );

      if (res.code === 200 && res.data) {
        setSchedules({
          can: res.data.can ? res.data.can : 0,
          doctors: res.data.doctors ? res.data.doctors : [],
          schedules: res.data.schedules ? formatSchedules(res.data.schedules) : []
        });
        getOptionFilter(res.data.doctors ? res.data.doctors : []);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const filterDoctor = (doctorid: number) => {
    return optionFilterSelected.length === 0 || optionFilterSelected.includes(doctorid);
  };

  const getOptionFilter = (doctors: any[]) => {
    const newOptionFilter = doctors.map((item: any) => ({
      id: item['id'],
      name: item['name'],
      type: 'doctor',
    }));

    setOptionFilter(newOptionFilter);
  };

  useEffect(() => {
    getCalendarMonth(props.yearMonth);
    // eslint-disable-next-line
  }, [props.yearMonth]);

  return (
    <Paper
      sx={{
        bgcolor: store.palette.container.paper,
        borderRadius: '16px',
        mt: '10px',
        height: 'calc(100vh - 150px)',
        width: props.openFilter ? 'calc(100vw - 355px)' : 'calc(100vw - 190px)',
        overflow: 'hidden',
      }}
    >
      {!isLoading ? (
        <TableContainer
          sx={{
            boxShadow: 'none',
            '& *': { color: 'primary.dark', fontSize: '12px' },
            width: '100%',
            minWidth: 'calc(100vw - 355px)',
            height: '100%',
          }}
        >
          <Table
            stickyHeader
            sx={{
              '& .MuiTableCell-root.MuiTableCell-head': {
                borderBottom: `1px solid ${colorBorderTableHead}`,
                borderRight: `1px solid ${colorBorderTableHead}`,
                fontWeight: 700,
                fontSize: '12px',
                p: '0px 8px',
                overflow: 'auto',
                bgcolor: '#E6E9ED'
              },
              '& .MuiTableCell-root': {
                borderRight: `1px solid ${colorBorderTableBody}`,
                borderBottom: `1px solid ${colorBorderTableBody}`,
              },
              '& .MuiTableCell-root.MuiTableCell-body': {
                p: '0px',
                // bgcolor: '#FFFFFF'
              },
            }}
            aria-label='simple table'
          >
            <TableHead>
              <TableRow sx={{ height: '36px', ...stickyHeader }}>
                {days.map((day, index: number) => (
                  <TableCell
                    key={index}
                    align='center'
                    width={'14.2%'}
                  >
                    <Typography
                      sx={{
                        fontWeight: '400',
                        lineHeight: '14px',
                        fontSize: '12px',
                        color: index === 0 ? '#FC6E51' : index === days.length - 1 ? '#5D9CEC' : '#656D78'
                      }}
                      component='pre'
                    >
                      {day}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {schedules && schedules.schedules &&
                schedules.schedules.map((weekData: any[], index: number) => (
                  <TableRow
                    key={index}
                  >
                    {weekData.map((dayData: any, indexDay: number) => (
                      <TableCell
                        key={dayData.label}
                        sx={{
                          fontSize: '12px',
                          fontWeight: 400,
                          lineWeight: '14.4px',
                          verticalAlign: 'top',
                          position: 'relative',
                          overflow: 'visible',
                          bgcolor: props.dateSelected === dayData.day ? '#E5F6FD !important' : ''
                        }}
                      >
                        <Box
                          sx={{
                            mt: '5px',
                            mb: props.dateSelected === dayData.day ? '6px' : '10px',
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          <Box
                            sx={{
                              ...(props.dateSelected === dayData.day
                                ? {
                                  bgcolor: '#23B7E5',
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '20px',
                                }
                                : {}),
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: '700',
                                lineHeight: '12px',
                                fontSize: '12px',
                                alignContent: 'center',
                                color: checkDateDiffMonth(dayData.day) ? '#CCD1D9'
                                  : props.dateSelected === dayData.day ? '#FFFFFF'
                                    : indexDay === 0 ? '#FC6E51'
                                      : indexDay === weekData.length - 1 ? '#5D9CEC' : '#434154'
                              }}
                              component='label'
                            >
                              {checkDiffMonth(weekData, indexDay) ? dayjs(dayData.day).format('M/D') : dayData.label}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1px',
                            mb: '10px',
                            // '&:hover': {
                            //   backgroundColor: 'lightgray',
                            //   // transform: 'scale(1.02)',
                            //   transition: 'all 0.3s',
                            // }
                          }}
                        >
                          {Object.entries(dayData.doctors).length !== 0
                            ? schedules.doctors.map((doctor: any) => (
                              dayData.doctors[doctor['id']] && filterDoctor(doctor['id']) ? (
                                <Box
                                  key={index + '-' + doctor.id}
                                  sx={{
                                    height: `${heightLine}px`,
                                    width: checkHasDoctorNext(weekData, indexDay, doctor.id) ? '110%' : '100%',
                                    pl: checkHasDoctorPrev(weekData, indexDay, doctor.id) ? '0px' : '8px',
                                    pr: checkHasDoctorNext(weekData, indexDay, doctor.id) ? '0px' : '8px'
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: checkHasDoctorNext(weekData, indexDay, doctor.id) ? '110%' : '100%',
                                      bgcolor: doctor.bgcolor ? doctor.bgcolor : 'red',
                                      pl: '8px',
                                      borderRadius: checkHasDoctorPrev(weekData, indexDay, doctor.id)
                                        ? '0px 4px 4px 0px' : '4px',
                                      display: 'flex',
                                      cursor: 'pointer'
                                    }}
                                    onClick={handleOpenDialogEdit(weekData, indexDay, doctor.id)}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: '700',
                                        lineHeight: '12px',
                                        fontSize: '10px',
                                        // color: doctor.fontcolor ? doctor.fontcolor : '#FFFFFF',
                                        color: '#FFFFFF',
                                        alignContent: 'center',
                                        cursor: 'pointer'
                                      }}
                                      component='label'
                                    >
                                      {checkHasDoctorPrev(weekData, indexDay, doctor.id) ? '' : doctor.name}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Box
                                  key={index + '-' + doctor.id}
                                  sx={{
                                    height: `${heightLine}px`,
                                    width: '100%',
                                    cursor: 'pointer'
                                  }}
                                  onClick={handleOpenDialog(getField(dayData, 'day', ''))}
                                />
                              )
                            )) : (
                              <Box
                                sx={{
                                  height: `${heightLine * schedules.doctors.length}px`,
                                  width: '100%',
                                  cursor: 'pointer'
                                }}
                                onClick={handleOpenDialog(getField(dayData, 'day', ''))}
                              />
                            )
                          }
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Skeleton variant='rectangular' width={'100%'} height={540} />
      )}
      <DialogFormSchedule
        open={dataShowDialogForm.open}
        data={dataShowDialogForm.data}
        doctors={getField(schedules, 'doctors', [])}
        yearMonth={props.yearMonth}
        onClose={handleCloseDialog}
      />
    </Paper>
  );
};
const WorkSchduleMemo = memo(WorkSchdule);

export default WorkSchduleMemo;
